import { useEffect, useState } from "react";
import { X, Maximize2 } from "lucide-react";

/**
 * ZoomableImage
 * - Renders a fluid, hover-zoom image inside an aspect-ratio box.
 * - Click to open in a full-screen lightbox.
 * - Caption rendered below as <figcaption>.
 *
 * Required: src, alt
 * Optional: caption, aspect (tailwind aspect-* class), imgClassName, figureClassName,
 *           captionOverline (uppercase label rendered above caption), grayscale.
 */
export default function ZoomableImage({
  src,
  alt,
  caption,
  captionOverline,
  aspect = "aspect-[4/3]",
  imgClassName = "",
  figureClassName = "",
  rounded = false,
  testId,
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <figure className={figureClassName} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`group relative w-full ${aspect} overflow-hidden border border-paperline bg-cream-deep cursor-zoom-in block ${
          rounded ? "rounded-sm" : ""
        }`}
        aria-label={`Open larger view: ${alt}`}
      >
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04] ${imgClassName}`}
        />
        <span className="absolute top-3 right-3 w-10 h-10 bg-cream/90 backdrop-blur flex items-center justify-center text-ink opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <Maximize2 size={14} />
        </span>
      </button>
      {(captionOverline || caption) && (
        <figcaption className="mt-4">
          {captionOverline && (
            <span className="overline block mb-1">{captionOverline}</span>
          )}
          {caption && (
            <span className="block text-sm md:text-[15px] italic font-serif text-ink/70 leading-relaxed max-w-2xl">
              {caption}
            </span>
          )}
        </figcaption>
      )}

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center p-4 sm:p-10 animate-fade-in"
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-label="Close"
            data-testid="lightbox-close"
            className="absolute top-5 right-5 w-11 h-11 border border-cream/40 text-cream hover:bg-cream hover:text-ink transition-colors flex items-center justify-center"
          >
            <X size={18} />
          </button>
          <img
            src={src}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain"
          />
          {caption && (
            <p className="absolute bottom-6 left-0 right-0 text-center text-cream/80 italic font-serif px-6 max-w-3xl mx-auto text-sm md:text-base">
              {caption}
            </p>
          )}
        </div>
      )}
    </figure>
  );
}
